import { formatISO, isAfter, isBefore, isSameDay } from 'date-fns';

export function gqlItemMap<T>(container: { items: (T | null)[] } | null | undefined): T[];
export function gqlItemMap<T, U>(container: { items: (T | null)[] } | null | undefined, mapper: (val: T) => U): U[];
export function gqlItemMap(
  container: { items: (unknown | null)[] } | null | undefined,
  mapper?: (val: unknown) => unknown,
): unknown[] {
  const result: unknown[] = [];

  if (container) {
    for (const item of container.items) {
      if (!item) continue;
      result.push(mapper ? mapper(item) : item);
    }
  }

  return result;
}

export function partition<T>(list: T[], select: (item: T) => 0 | 1): [T[], T[]] {
  const results: [T[], T[]] = [[], []];

  for (let i = 0; i < list.length; i++) {
    results[select(list[i])].push(list[i]);
  }

  return results;
}

export function formatISODate(value: number | Date) {
  return formatISO(value, { representation: 'date' });
}

export function today() {
  return formatISODate(Date.now());
}

/**
 * Is value on or after otherDate?
 * @param value
 * @param otherDate
 * @returns true if value is on or after otherDate, false if value is before otherDate, undefined if otherDate is undefined
 */
export function isDateValueOnOrAfter(value: Date, otherDate: Date | undefined) {
  if (!otherDate) return undefined;
  return isSameDay(value, otherDate) || isAfter(value, otherDate);
}

/**
 * Is value on or before otherDate?
 * @param value
 * @param otherDate
 * @returns true if value is on or before otherDate, false if value is after otherDate, undefined if otherDate is undefined
 */
export function isDateValueOnOrBefore(value: Date, otherDate: Date | undefined) {
  if (!otherDate) return undefined;
  return isSameDay(value, otherDate) || isBefore(value, otherDate);
}

export function pick<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const ret: any = {};
  for (const key of keys) {
    ret[key] = obj[key];
  }
  return ret;
}
